import { dataFetcher } from '../../dataFetcher'
import { parseImageSource } from '../../utils'

export interface StoriesResponse {
  aggregations: {
    type: string
    values: { id: string; title: string; count: string }[]
  }[]
  resultInfo: { totalRecords: number; selectedRecords: number }
  results: {
    id: string
    storyId: number
    title: string
    subTitle: string
    subTitleHTML: string
    storySlug: string
    storyDate: string
    publishDate: string
    flags: {
      hideInArchive: boolean
      showVideoArrow: boolean
    }
    media: [
      {
        type: 'image'
        alt: string
        description: string
        primary: {
          href: string
        }
      }
    ]
    tags: {
      type: 'storyType' | 'storyCategory'
      values: { id: string; value: string }[]
    }[]
  }[]
}

export interface Result {
  id: string
  primaryTitle: string
  secondaryTitle: string
  type?: string
  category?: string
  cta: { href: string; target?: string }
  image: { src: string; alt: string }
}

export type Results = Result[]

export interface ParsedStoriesResponse {
  filterGroups: {
    id: string
    title: string
    filters: {
      id: string
      text: string
      count: string
    }[]
  }[]
  resultInfo: { totalRecords: number; selectedRecords: number }
  results: Results
}

export interface Endpoint {
  url: string
  method: 'GET' | 'POST'
  parameters?: Record<string, string>
  headers?: Record<string, string>
  credentials?: RequestCredentials
}

export const fetcher = async (endpoint: Endpoint): Promise<ParsedStoriesResponse | undefined> => {
  const response = await dataFetcher<StoriesResponse>(endpoint)

  if (!response) return
  return {
    filterGroups: response.aggregations.map((aggregation) => ({
      id: aggregation.type,
      title: aggregation.type,
      filters: aggregation.values.map(({ id, title, count }) => ({
        id,
        text: title,
        count,
      })),
    })),
    results: response.results.map((result) => {
      const image = result?.media?.find((media) => media.type.toLowerCase() === 'image')
      const category = result?.tags?.find((tag) => tag.type === 'storyCategory')?.values?.[0]?.value
      const type = result?.tags?.find((tag) => tag.type === 'storyType')?.values?.[0]?.value
      return {
        id: result.id,
        primaryTitle: result.title,
        secondaryTitle: result.subTitleHTML,
        type: type,
        category: category,
        cta: { href: `/stories/${result.storySlug}-${result.id}` },
        // appending hash for sitecore image resizing
        image: { src: image ? `${parseImageSource(image?.primary?.href)}?hash=` : '', alt: image?.alt || '' },
        thumbnail: result.flags?.showVideoArrow,
      }
    }),
    resultInfo: response.resultInfo,
  }
}
